/**
 * La bibliothèque de profils du mode `?demo` (ARTO-130) : deux profils fournis
 * et un profil d'exploitation à deux versions, dont la seconde corrige la
 * première. Un lot qui cite la version 1 continue de la citer. Des règles
 * fictives, sans valeur propre à une galerie.
 */
import type { ChampRegle, ProfilComplet, Regles } from './correspondance.js';
import { CIBLES_DEMO } from './demoCibles.js';
import { profilsEditeursDemo } from './demoProfilsEditeurs.js';

const il_y_a = (jours: number): string => new Date(Date.now() - jours * 86_400_000).toISOString();

const colonne = (champ: string, ...suite: ChampRegle['transformations']): ChampRegle => ({
  sources: [{ champ, role: null, parent: false }],
  separateur: ' ',
  transformations: suite,
});

/** Chaque colonne du gabarit lit l'en-tête du gabarit : l'export d'Artosera. */
function reglesArtosera(): Regles {
  const colonnes: Regles['natures'][string]['colonnes'] = {};
  const cible = CIBLES_DEMO.natures.find((candidate) => candidate.nature === 'oeuvres');
  for (const entree of cible?.colonnes ?? []) colonnes[entree.cle] = colonne(entree.entete);
  return {
    sources: [{ role: 'principal', feuille: null, racine: null }],
    jointures: [],
    deplier: null,
    filtres: [],
    natures: { oeuvres: { colonnes, cleOrigine: null, filtres: [], dedoublonner: null } },
  };
}

function reglesVCard(): Regles {
  return {
    sources: [{ role: 'principal', feuille: null, racine: null }],
    jointures: [],
    deplier: null,
    filtres: [],
    natures: {
      contacts: {
        colonnes: {
          nom: colonne('N.famille'),
          prenom: colonne('N.prenom'),
          email: colonne('EMAIL[0]'),
          telephones: colonne('TEL[*]'),
        },
        cleOrigine: null,
        filtres: [],
        dedoublonner: null,
      },
    },
  };
}

function reglesExport(version: 1 | 2): Regles {
  const artiste: ChampRegle = {
    sources: [
      { champ: 'Prénom artiste', role: null, parent: false },
      { champ: 'Nom artiste', role: null, parent: false },
    ],
    separateur: ' ',
    transformations: [{ type: 'titre' }],
  };
  const oeuvres: Regles['natures'][string] = {
    colonnes: {
      numeroInventaire: colonne('Réf'),
      titre: colonne('Titre'),
      artiste,
      techniqueFr: colonne('Technique'),
      prixPublic: colonne('Prix TTC', { type: 'montant', decimal: ',', devise: 'EUR' }),
    },
    cleOrigine: null,
    filtres: [
      {
        source: { champ: 'Statut', role: null, parent: false },
        operateur: 'different',
        valeur: 'archivé',
      },
    ],
    dedoublonner: null,
  };
  if (version === 2) {
    oeuvres.colonnes['statut'] = colonne('Statut', {
      type: 'dictionnaire',
      valeurs: { 'En stock': 'disponible', Vendu: 'vendue', Prêté: 'prêtée' },
    });
    oeuvres.colonnes['hauteur'] = colonne(
      'Dimensions',
      { type: 'scinder', separateur: ' x ', index: 1 },
      { type: 'extraire_nombre' },
    );
    oeuvres.colonnes['largeur'] = colonne(
      'Dimensions',
      { type: 'scinder', separateur: ' x ', index: 0 },
      { type: 'extraire_nombre' },
    );
    oeuvres.colonnes['localisation'] = colonne('Emplacement');
  }
  return {
    sources: [{ role: 'principal', feuille: null, racine: null }],
    jointures: [],
    deplier: null,
    filtres: [],
    natures: {
      oeuvres,
      artistes: {
        colonnes: {
          prenom: colonne('Prénom artiste', { type: 'titre' }),
          nom: colonne('Nom artiste', { type: 'titre' }),
        },
        cleOrigine: null,
        filtres: [],
        dedoublonner: 'nom',
      },
    },
  };
}

const SIGNATURE_EXPORT = [
  'Réf',
  'Titre',
  'Prénom artiste',
  'Nom artiste',
  'Technique',
  'Dimensions',
  'Prix TTC',
  'Statut',
  'Emplacement',
];

export const ID_PROFIL_EXPORT = 'profil-demo-export-v3';
export const ID_PROFIL_ARTOSERA = 'profil-demo-artosera-oeuvres';
export const ID_PROFIL_VCARD = 'profil-demo-vcard';

/** Les versions de départ de la bibliothèque. */
export function profilsDeDepart(): ProfilComplet[] {
  const base = (
    sur: Omit<ProfilComplet, 'natures' | 'creeLe'> & { creeIlYA: number },
  ): ProfilComplet => {
    const { creeIlYA, ...reste } = sur;
    return {
      ...reste,
      natures: Object.keys(reste.regles.natures),
      creeLe: il_y_a(creeIlYA),
    };
  };
  return [
    base({
      id: ID_PROFIL_ARTOSERA,
      version: 1,
      nom: 'Artosera → Artosera (œuvres)',
      application: 'Artosera',
      nature: 'oeuvres',
      signature: (CIBLES_DEMO.natures[2]?.colonnes ?? []).map((entree) => entree.entete),
      fourni: true,
      auteur: null,
      regles: reglesArtosera(),
      creeIlYA: 60,
    }),
    base({
      id: ID_PROFIL_VCARD,
      version: 1,
      nom: 'vCard (contacts)',
      application: 'Carnets d’adresses (vCard)',
      nature: 'contacts',
      signature: ['N', 'FN', 'EMAIL', 'TEL'],
      fourni: true,
      auteur: null,
      regles: reglesVCard(),
      creeIlYA: 60,
    }),
    base({
      id: ID_PROFIL_EXPORT,
      version: 1,
      nom: 'Export d’œuvres, logiciel de gestion (v3)',
      application: 'Logiciel de gestion de galerie',
      nature: 'oeuvres',
      signature: SIGNATURE_EXPORT,
      fourni: false,
      auteur: 'exploitation@venio.test',
      regles: reglesExport(1),
      creeIlYA: 21,
    }),
    base({
      id: ID_PROFIL_EXPORT,
      version: 2,
      nom: 'Export d’œuvres, logiciel de gestion (v3)',
      application: 'Logiciel de gestion de galerie',
      nature: 'oeuvres',
      signature: SIGNATURE_EXPORT,
      fourni: false,
      auteur: 'exploitation@venio.test',
      regles: reglesExport(2),
      creeIlYA: 4,
    }),
    ...profilsEditeursDemo(il_y_a(30)),
  ];
}
